/* Shared product search + filter system used by the Add-products drawer and the
   My Collection page: free-text search, Category (Category6 range), Suitable-for
   and Free-from chips. Keeps both surfaces consistent. */
import { useMemo, useState } from 'react';
import { Ico } from './Icons';
import { ALLERGENS, SUITABILITY, rangesOf } from '@/portal/data/product';
import type { PortalProduct } from '@/portal/api/types';

export interface ProductFilterState {
  q: string;
  setQ: (v: string) => void;
  cat: string;
  setCat: (v: string) => void;
  suit: string[];
  free: string[];
  toggleSuit: (v: string) => void;
  toggleFree: (v: string) => void;
  clear: () => void;
  hasFilters: boolean;
}

export function useProductFilters(): ProductFilterState {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [suit, setSuit] = useState<string[]>([]);
  const [free, setFree] = useState<string[]>([]);

  const toggle = (arr: string[], set: (v: string[]) => void, v: string) =>
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  return {
    q,
    setQ,
    cat,
    setCat,
    suit,
    free,
    toggleSuit: (v) => toggle(suit, setSuit, v),
    toggleFree: (v) => toggle(free, setFree, v),
    clear: () => {
      setCat('');
      setSuit([]);
      setFree([]);
    },
    hasFilters: cat !== '' || suit.length > 0 || free.length > 0,
  };
}

/** Apply the active filters to a product set. */
export function applyProductFilters(products: PortalProduct[], s: ProductFilterState): PortalProduct[] {
  const query = s.q.trim().toLowerCase();
  return products.filter(
    (p) =>
      (!query || p.name.toLowerCase().includes(query) || p.code.toLowerCase().includes(query)) &&
      (s.cat === '' || p.rangeKey === s.cat) &&
      s.suit.every((x) => p.suitability.includes(x)) &&
      s.free.every((a) => !p.allergens.includes(a)),
  );
}

/** Filter controls. `source` is the full set used to derive the category list. */
export function ProductFilters({
  state,
  source,
  matchCount,
  showSearch = false,
  hideFooter = false,
  searchPlaceholder = 'Search your collection…',
}: {
  state: ProductFilterState;
  source: PortalProduct[];
  matchCount: number;
  showSearch?: boolean;
  hideFooter?: boolean;
  searchPlaceholder?: string;
}) {
  const cats = useMemo(() => rangesOf(source), [source]);

  return (
    <>
      {showSearch && (
        <div className="pl-search" style={{ marginBottom: 16 }}>
          <Ico.search width={16} height={16} />
          <input placeholder={searchPlaceholder} value={state.q} onChange={(e) => state.setQ(e.target.value)} />
        </div>
      )}

      <div className="pl-filter-row">
        <div className="pl-filter-cat">
          <div className="pl-filter-label">Category</div>
          <select
            className="pl-field"
            style={{ marginBottom: 0 }}
            value={state.cat}
            onChange={(e) => state.setCat(e.target.value)}
          >
            <option value="">All categories</option>
            {cats.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <div className="pl-filter-label">Suitable for</div>
        <div className="pl-fchips">
          {SUITABILITY.map((s) => (
            <button
              key={s}
              className={'pl-fchip' + (state.suit.includes(s) ? ' on' : '')}
              onClick={() => state.toggleSuit(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <div className="pl-filter-label">Free from</div>
        <div className="pl-fchips">
          {ALLERGENS.map((a) => (
            <button
              key={a}
              className={'pl-fchip free' + (state.free.includes(a) ? ' on' : '')}
              onClick={() => state.toggleFree(a)}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {!hideFooter && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 13, color: 'var(--ink-faint)' }}>{matchCount} match</span>
          {state.hasFilters && (
            <button className="pl-btn ghost sm" onClick={state.clear}>
              Clear filters
            </button>
          )}
        </div>
      )}
    </>
  );
}
